import React, { useCallback, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faExpand,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

interface ImageCarouselProps {
  images: string[];
}

/**
 * Circular, semi-transparent dark button with a white glyph so the controls
 * stay legible on the very light seam-detection captures the hub uploads
 * (the library's default arrows are white-on-transparent and vanish there).
 */
const overlayButtonSx = {
  color: '#FFFFFF',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.4)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
  '&.Mui-focusVisible': {
    outline: '2px solid #FFFFFF',
    outlineOffset: '2px',
  },
};

const carouselArrowSx = {
  ...overlayButtonSx,
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  width: 40,
  height: 40,
};

const lightboxArrowSx = {
  ...overlayButtonSx,
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 56,
  height: 56,
};

/**
 * Shared image viewer (Operations, hub Seam Detection Images, med event
 * Medication images). Shows a thumbnail-strip carousel; clicking the centre
 * image (or the expand button) opens the current image full screen with its
 * own prev / next controls. The two views share one index so the carousel
 * lands on whatever image was being viewed when the lightbox closes.
 */
const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const total = images.length;
  // Guard against a shrinking `images` prop leaving the index out of range
  const currentIndex = Math.min(selectedIndex, Math.max(total - 1, 0));

  const showPrevious = useCallback(
    () => setSelectedIndex((i) => Math.max(i - 1, 0)),
    [],
  );
  const showNext = useCallback(
    () => setSelectedIndex((i) => Math.min(i + 1, total - 1)),
    [total],
  );

  const handleLightboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') showPrevious();
    else if (e.key === 'ArrowRight') showNext();
  };

  if (!total) return null;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          // The click-to-expand affordance lives on the slide (library CSS
          // sets pointer-events: none on the img itself)
          '& .carousel .slide': { cursor: 'zoom-in' },
          // Make the "n of N" status readable on light images too
          '& .carousel .carousel-status': {
            fontSize: '12px',
            padding: '4px 8px',
            margin: '8px',
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            textShadow: 'none',
          },
        }}
      >
        <Carousel
          selectedItem={currentIndex}
          onChange={setSelectedIndex}
          onClickItem={() => setIsLightboxOpen(true)}
          useKeyboardArrows
          renderArrowPrev={(onClick, hasPrev, label) =>
            hasPrev ? (
              <IconButton
                aria-label={label}
                onClick={onClick}
                sx={{ ...carouselArrowSx, left: 8 }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </IconButton>
            ) : null
          }
          renderArrowNext={(onClick, hasNext, label) =>
            hasNext ? (
              <IconButton
                aria-label={label}
                onClick={onClick}
                sx={{ ...carouselArrowSx, right: 8 }}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
            ) : null
          }
        >
          {images.map((img, index) => (
            <div key={index}>
              <img src={img} alt={`${index + 1}`} />
            </div>
          ))}
        </Carousel>
        <Tooltip title="View full screen">
          <IconButton
            aria-label="View full screen"
            onClick={() => setIsLightboxOpen(true)}
            size="small"
            sx={{
              ...overlayButtonSx,
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              width: 32,
              height: 32,
            }}
          >
            <FontAwesomeIcon icon={faExpand} style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        fullScreen
        onKeyDown={handleLightboxKeyDown}
        PaperProps={{
          'aria-label': 'Full screen image viewer',
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <IconButton
          aria-label="Close full screen"
          onClick={() => setIsLightboxOpen(false)}
          sx={{
            ...overlayButtonSx,
            position: 'absolute',
            top: 16,
            right: 16,
            width: 44,
            height: 44,
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>

        <Typography
          sx={{
            position: 'absolute',
            top: 24,
            left: 24,
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 500,
            padding: '4px 10px',
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
          }}
          data-testid="lightbox-status"
        >
          {`${currentIndex + 1} of ${total}`}
        </Typography>

        <Box
          component="img"
          src={images[currentIndex]}
          alt={`${currentIndex + 1} of ${total}, full screen`}
          onClick={() => setIsLightboxOpen(false)}
          sx={{
            maxWidth: '100vw',
            maxHeight: '100vh',
            objectFit: 'contain',
            cursor: 'zoom-out',
            userSelect: 'none',
          }}
        />

        {currentIndex > 0 && (
          <IconButton
            aria-label="previous image"
            onClick={showPrevious}
            sx={{ ...lightboxArrowSx, left: 24 }}
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 22 }} />
          </IconButton>
        )}
        {currentIndex < total - 1 && (
          <IconButton
            aria-label="next image"
            onClick={showNext}
            sx={{ ...lightboxArrowSx, right: 24 }}
          >
            <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 22 }} />
          </IconButton>
        )}
      </Dialog>
    </>
  );
};

export default ImageCarousel;
